import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../../components/useBreadcrumbBack";
import { useAuth } from "../../../../providers/AuthProvider";
import { ApiError } from "../../../../services/apiClient";
import {
  listAuditLogs,
  type AuditLog,
  type ActorType,
} from "../../../shared/api/auditLogs";

import { PageShell } from "../../../shared/layouts/PageShell";
import { PageHeader } from "../../../shared/ui/layouts/PageHeader";

import {
  ArrowLeft,
  ArrowsClockwise,
  CaretLeft,
  CaretRight,
  FunnelSimple,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../../shared/ui/icons";

interface FilterState {
  workspaceId: string;
  actorId: string;
  action: string;
  resourceType: string;
  actorType: string;
  createdAfter: string;
  createdBefore: string;
}

const initialFilters: FilterState = {
  workspaceId: "",
  actorId: "",
  action: "",
  resourceType: "",
  actorType: "",
  createdAfter: "",
  createdBefore: "",
};

const pageSizeOptions = [25, 50, 100] as const;
const actorTypeOptions: (ActorType | "")[] = ["", "user", "service", "api_key", "system"];
const actionOptions: string[] = [
  "workspace.member_added",
  "workspace.member_removed",
  "workspace.role_changed",
  "workspace.invite_sent",
  "workspace.created",
  "workspace.updated",
  "workspace.archived",
  "workspace.restored",
  "control_point.created",
  "control_point.deleted",
  "control_point.updated",
  "lot.created",
  "lot.updated",
  "lot.archived",
  "lot.restored",
  "file.revision_created",
  "file.restored",
  "file.deleted",
  "file.uploaded",
  "auth.login_succeeded",
  "auth.login_failed",
  "auth.mfa_challenge",
  "auth.logout",
  "auth.password_reset",
  "auth.password_changed",
  "auth.phone_verification_requested",
  "auth.phone_verified",
  "auth.email_verified",
  "auth.registered",
  "profile.address_updated",
];

const resourceTypeOptions: string[] = [
  "workspace",
  "workspace_member",
  "workspace_invitation",
  "control_point",
  "lot",
  "file",
  "user",
];

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const cardClasses = "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const primaryButtonClasses = "pm-btn pm-btn-primary";
const inputClasses =
  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";
const filterLabelClasses = "flex flex-col gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-300";

export function AdminAuditLogsPage() {
  const { t } = useTranslation();
  const { user, accessToken } = useAuth();

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [activeFilters, setActiveFilters] =
    useState<FilterState>(initialFilters);
  const [limit, setLimit] = useState<number>(50);
  const [offset, setOffset] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("admin.title"), to: "/admin" },
      { label: t("admin.audit_logs.list_title") },
    ];
  }, [t]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/admin" },
  });

  const dateFormatter = useMemo(() => {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }, []);

  const formatDate = useCallback(
    (value?: string | null) => {
      if (!value) {
        return t("common.date_unknown");
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return dateFormatter.format(date);
    },
    [dateFormatter, t],
  );

  const toISOString = (value?: string) => {
    if (!value) {
      return undefined;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return undefined;
    }
    return date.toISOString();
  };

  const loadLogs = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const response = await listAuditLogs(accessToken, {
        workspace_id: activeFilters.workspaceId.trim() || undefined,
        actor_id: activeFilters.actorId.trim() || undefined,
        action: activeFilters.action.trim() || undefined,
        resource_type: activeFilters.resourceType.trim() || undefined,
        actor_type: (activeFilters.actorType as ActorType) || undefined,
        created_after: toISOString(activeFilters.createdAfter),
        created_before: toISOString(activeFilters.createdBefore),
        limit,
        offset,
        sort_desc: true,
      });

      setLogs(response.logs ?? []);
      setTotalCount(response.total_count ?? 0);
      setLastSyncedAt(Date.now());
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("admin.audit_logs.load_error");
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, activeFilters, limit, offset, t]);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  if (!user || !accessToken) {
    return null;
  }

  if (user.system_role !== "admin") {
    return (
      <PageShell className={pageStackClasses} width="wide">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={t("common.access_denied.title")}
          subtitle={t("common.access_denied.message")}
          actions={
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={handleBack}
            >
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          }
        />
      </PageShell>
    );
  }

  const handleFilterInput =
    (key: keyof FilterState) => (value: string) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    };

  const handleFiltersSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActiveFilters(filters);
    setOffset(0);
  };

  const handleResetFilters = () => {
    setFilters(initialFilters);
    setActiveFilters(initialFilters);
    setOffset(0);
  };

  const handleLimitChange = (value: number) => {
    setLimit(value);
    setOffset(0);
  };

  const handlePreviousPage = () => {
    setOffset((current) => Math.max(0, current - limit));
  };

  const handleNextPage = () => {
    setOffset((current) => current + limit);
  };

  const handleRefresh = () => {
    void loadLogs();
  };

  const hasPrevious = offset > 0;
  const hasNext = offset + logs.length < totalCount;
  const start = logs.length === 0 ? 0 : offset + 1;
  const end = logs.length === 0 ? 0 : offset + logs.length;

  const formatMetadataValue = (value: unknown) => {
    if (value === null || value === undefined) {
      return "—";
    }
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return "[object]";
      }
    }
    return String(value);
  };

  const renderMetadata = (metadata?: Record<string, unknown> | null) => {
    const entries = Object.entries(metadata ?? {}).filter(
      ([key]) => typeof key === "string",
    );

    if (entries.length === 0) {
      return (
        <span className="text-xs italic text-slate-400">
          {t("admin.audit_logs.table.none")}
        </span>
      );
    }

    const primaryEntries = entries.slice(0, 3);
    const remaining = entries.length - primaryEntries.length;

    return (
      <div
        className="flex flex-col gap-1 text-xs"
        title={JSON.stringify(metadata, null, 2)}
      >
        {primaryEntries.map(([key, value]) => (
          <span key={key} className="flex gap-1">
            <strong className="font-semibold text-slate-700">{key}:</strong>{" "}
            <span className="text-slate-600 truncate max-w-[200px]">{formatMetadataValue(value)}</span>
          </span>
        ))}
        {remaining > 0 ? (
          <span className="text-slate-400 italic">
            {t("admin.audit_logs.table.metadata_more", { count: remaining })}
          </span>
        ) : null}
      </div>
    );
  };

  const formatActor = (log: AuditLog) => {
    if (log.actor_id) {
      return `${log.actor_type} • ${log.actor_id.slice(0, 8)}…`;
    }
    return log.actor_type;
  };

  const formatResource = (log: AuditLog) => {
    if (log.resource_id) {
      return `${log.resource_type} • ${log.resource_id.slice(0, 8)}…`;
    }
    return log.resource_type || t("admin.audit_logs.table.resource_unknown");
  };

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />

      <PageHeader
        title={t("admin.audit_logs.list_title")}
        subtitle={t("admin.audit_logs.list_subtitle")}
        actions={
          <button
            type="button"
            className={ghostButtonClasses}
            onClick={handleBack}
          >
            <ArrowLeft {...defaultIconProps} aria-hidden />
            {t("actions.back")}
          </button>
        }
      />

      <form
        className={cardClasses}
        onSubmit={handleFiltersSubmit}
      >
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
              <FunnelSimple {...defaultIconProps} aria-hidden />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{t("admin.audit_logs.filters.title")}</h2>
              <p className="text-sm text-slate-500">{t("admin.audit_logs.filters.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" className={primaryButtonClasses}>
              {t("admin.audit_logs.filters.apply")}
            </button>
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={handleResetFilters}
            >
              {t("admin.audit_logs.filters.reset")}
            </button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <label className={filterLabelClasses}>
            <span>{t("admin.audit_logs.filters.workspace_id")}</span>
            <input
              type="text"
              className={inputClasses}
              value={filters.workspaceId}
              onChange={(event) =>
                handleFilterInput("workspaceId")(event.target.value)
              }
              placeholder="d8fb2c5e-…"
            />
          </label>

          <label className={filterLabelClasses}>
            <span>{t("admin.audit_logs.filters.actor_id")}</span>
            <input
              type="text"
              className={inputClasses}
              value={filters.actorId}
              onChange={(event) =>
                handleFilterInput("actorId")(event.target.value)
              }
              placeholder="user uuid"
            />
          </label>

          <label className={filterLabelClasses}>
            <span>{t("admin.audit_logs.filters.actor_type")}</span>
            <select
              className={inputClasses}
              value={filters.actorType}
              onChange={(event) =>
                handleFilterInput("actorType")(event.target.value)
              }
            >
              <option value="">
                {t("admin.audit_logs.filters.actor_type_any")}
              </option>
              {actorTypeOptions
                .filter((option) => option !== "")
                .map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
            </select>
          </label>

          <label className={filterLabelClasses}>
            <span>{t("admin.audit_logs.filters.action")}</span>
            <input
              type="text"
              className={inputClasses}
              value={filters.action}
              onChange={(event) =>
                handleFilterInput("action")(event.target.value)
              }
              list="audit-actions"
              placeholder="workspace.member_added"
            />
            <datalist id="audit-actions">
              {actionOptions.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          </label>

          <label className={filterLabelClasses}>
            <span>{t("admin.audit_logs.filters.resource_type")}</span>
            <input
              type="text"
              className={inputClasses}
              value={filters.resourceType}
              onChange={(event) =>
                handleFilterInput("resourceType")(event.target.value)
              }
              list="audit-resource-types"
              placeholder="workspace"
            />
            <datalist id="audit-resource-types">
              {resourceTypeOptions.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          </label>

          <label className={filterLabelClasses}>
            <span>{t("admin.audit_logs.filters.created_after")}</span>
            <input
              type="datetime-local"
              className={inputClasses}
              value={filters.createdAfter}
              onChange={(event) =>
                handleFilterInput("createdAfter")(event.target.value)
              }
            />
          </label>

          <label className={filterLabelClasses}>
            <span>{t("admin.audit_logs.filters.created_before")}</span>
            <input
              type="datetime-local"
              className={inputClasses}
              value={filters.createdBefore}
              onChange={(event) =>
                handleFilterInput("createdBefore")(event.target.value)
              }
            />
          </label>
        </div>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            {t("admin.audit_logs.limit_label")}
            <select
              className="rounded-lg border-0 py-1.5 pl-3 pr-8 text-sm shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-sky-600 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:focus:ring-sky-500"
              value={limit}
              onChange={(event) => handleLimitChange(Number(event.target.value))}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          {lastSyncedAt ? (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {t("admin.audit_logs.last_synced", {
                timestamp: formatDate(new Date(lastSyncedAt).toISOString()),
              })}
            </span>
          ) : null}
        </div>
        <div>
          <button
            type="button"
            className={ghostButtonClasses}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <ArrowsClockwise
              {...defaultIconProps}
              aria-hidden
              className={isLoading ? "motion-safe:animate-spin motion-reduce:animate-none" : undefined}
            />
            {t("admin.audit_logs.refresh")}
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300" role="alert">
          {error}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {isLoading ? (
          <p className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">
            {t("admin.audit_logs.loading")}
          </p>
        ) : logs.length === 0 ? (
          <p className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">
            {t("admin.audit_logs.empty_state")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-6 py-3">{t("admin.audit_logs.table.timestamp")}</th>
                  <th className="px-6 py-3">{t("admin.audit_logs.table.workspace")}</th>
                  <th className="px-6 py-3">{t("admin.audit_logs.table.actor")}</th>
                  <th className="px-6 py-3">{t("admin.audit_logs.table.action")}</th>
                  <th className="px-6 py-3">{t("admin.audit_logs.table.resource")}</th>
                  <th className="px-6 py-3">{t("admin.audit_logs.table.ip")}</th>
                  <th className="px-6 py-3">{t("admin.audit_logs.table.request_id")}</th>
                  <th className="px-6 py-3">{t("admin.audit_logs.table.metadata")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                    <td className="px-6 py-4 whitespace-nowrap dark:text-slate-300">{formatDate(log.occurred_at)}</td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-xs dark:text-slate-400">{log.workspace_id ?? t("common.table.empty_value")}</td>
                    <td className="px-6 py-4 whitespace-nowrap dark:text-slate-300">{formatActor(log)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">{log.action}</code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap dark:text-slate-300">{formatResource(log)}</td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-xs dark:text-slate-400">{log.ip_address ?? "—"}</td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-xs text-slate-400 dark:text-slate-500">{log.request_id ?? "—"}</td>
                    <td className="px-6 py-4 min-w-[200px]">{renderMetadata(log.metadata)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="text-sm text-slate-500 dark:text-slate-400">
          {t("admin.audit_logs.pagination.summary", {
            start,
            end,
            total: totalCount,
          })}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={ghostButtonClasses}
            onClick={handlePreviousPage}
            disabled={!hasPrevious || isLoading}
          >
            <CaretLeft {...defaultIconProps} aria-hidden />
            {t("admin.audit_logs.pagination.previous")}
          </button>
          <button
            type="button"
            className={ghostButtonClasses}
            onClick={handleNextPage}
            disabled={!hasNext || isLoading}
          >
            {t("admin.audit_logs.pagination.next")}
            <CaretRight {...defaultIconProps} aria-hidden />
          </button>
        </div>
      </div>
    </PageShell>
  );
}
